/**
 * Provisioning Runs API Contracts
 *
 * Request/response shapes for the provisioning orchestrator endpoints.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-05-09-003-feat-mobile-provisioning-orchestrator-plan.md (U2)
 */

import { z } from 'zod'
import { ProvisioningRunInputsSchema, ProvisioningRunResponseSchema } from '../../schemas/entities/provisioning'
import { BrowserSessionSlugSchema } from '../../schemas/entities/provisioning-browser-steps'
import {
  BrowserStepRunStatusSchema,
  ProvisioningDispatchResponseSchema,
  StepEvidenceKindSchema,
  StepEvidenceSchema
} from '../../schemas/entities/provisioning-dispatch'

// GET /v1/mobile-apps/provisioning-runs

export const ListProvisioningRunsRequestSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional(),
  /** Scope the list to one mobile app's runs (drives the app-detail Provisioning tab). */
  mobileAppId: z.string().min(1).optional()
})
export type ListProvisioningRunsRequest = z.infer<typeof ListProvisioningRunsRequestSchema>

export const ListProvisioningRunsResponseSchema = z.object({
  message: z.string(),
  data: z.array(ProvisioningRunResponseSchema),
  meta: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number()
  })
})
export type ListProvisioningRunsResponse = z.infer<typeof ListProvisioningRunsResponseSchema>

// POST /v1/mobile-apps/provisioning-runs

/**
 * `browserOnly` starts a run that exists ONLY as the ledger the Playwright script writes to.
 *
 * The two halves of provisioning need different secrets: the API steps need an App Store Connect
 * key / Google service account, while the browser steps need neither — they drive the vendor
 * portals through a stored, already-signed-in browser session. Requiring API credentials before
 * anyone can touch the browser half therefore blocks work that does not depend on them, which is
 * exactly the position an operator is in when they want to create the store listings by hand-off
 * to the script and nothing else.
 *
 * A browser-only run skips the inline-credential requirement, seeds its orchestrator steps as
 * SKIPPED, and is never advanced — so it makes no vendor API call at all.
 */
export const StartProvisioningRunRequestSchema = ProvisioningRunInputsSchema.extend({
  browserOnly: z.boolean().optional()
})
export type StartProvisioningRunRequest = z.infer<typeof StartProvisioningRunRequestSchema>

export const StartProvisioningRunResponseSchema = ProvisioningRunResponseSchema
export type StartProvisioningRunResponse = z.infer<typeof StartProvisioningRunResponseSchema>

// GET /v1/mobile-apps/provisioning-runs/:id

export const GetProvisioningRunRequestSchema = z.object({
  id: z.string().min(1)
})
export type GetProvisioningRunRequest = z.infer<typeof GetProvisioningRunRequestSchema>

export const GetProvisioningRunResponseSchema = ProvisioningRunResponseSchema
export type GetProvisioningRunResponse = z.infer<typeof GetProvisioningRunResponseSchema>

// POST /v1/mobile-apps/provisioning-runs/:id/steps/:name/retry

export const RetryProvisioningStepRequestSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1)
})
export type RetryProvisioningStepRequest = z.infer<typeof RetryProvisioningStepRequestSchema>

// POST /v1/mobile-apps/provisioning-runs/:id/steps/:name/manual-complete

export const ManualCompleteProvisioningStepRequestSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  note: z.string().optional(),
  output: z.unknown().optional()
})
export type ManualCompleteProvisioningStepRequest = z.infer<typeof ManualCompleteProvisioningStepRequestSchema>

// POST /v1/mobile-apps/provisioning-runs/:id/dispatches
//
// Hands a batch of browser steps to an automation runner. Omitting `stepIds`
// means "every manifest step whose deps are satisfied and which has not
// already succeeded". The dispatcher groups the resolved steps by browser
// session slug, creating one dispatch per slug so distinct portals run in
// parallel while a single trusted-browser session is never raced.

export const CreateProvisioningDispatchRequestSchema = z.object({
  stepIds: z.array(z.string().min(1)).optional(),
  // Probe only: the runner reports each step's state and writes nothing
  // (`--dry-run`). Absent means false. A live write to an Apple identifier that
  // holds provisioning profiles is dispatched only after a dry run named them
  // (SCH-1795 R5).
  dryRun: z.boolean().optional()
})
export type CreateProvisioningDispatchRequest = z.infer<typeof CreateProvisioningDispatchRequestSchema>

/**
 * One pre-flight refusal, as the launch module reports it: a team whose standing refuses the Apple
 * steps, or a stored session too stale to open. Loose, because each dimension carries its own
 * fields (`accountId` and `contact` for a team, `slug` for a session) and the console renders
 * `detail` alone.
 */
export const BrowserLaunchPreflightRefusalSchema = z
  .object({
    dimension: z.enum(['team-standing', 'session-freshness']),
    reason: z.string(),
    detail: z.string()
  })
  .loose()
export type BrowserLaunchPreflightRefusal = z.infer<typeof BrowserLaunchPreflightRefusalSchema>

/** A session slug pre-flight refused, with the steps it would have run. The other slugs still ran. */
export const BrowserLaunchPreflightRefusedSlugSchema = z.object({
  sessionSlug: BrowserSessionSlugSchema,
  stepIds: z.array(z.string()),
  refusals: z.array(BrowserLaunchPreflightRefusalSchema)
})
export type BrowserLaunchPreflightRefusedSlug = z.infer<typeof BrowserLaunchPreflightRefusedSlugSchema>

export const CreateProvisioningDispatchResponseSchema = z.object({
  message: z.string(),
  data: z.array(ProvisioningDispatchResponseSchema),
  /**
   * Slugs pre-flight refused while others dispatched (SCH-2964 R3). Absent when none was refused;
   * when EVERY slug is refused the route answers 422 `PREFLIGHT_REFUSED` instead.
   */
  preflightRefused: z.array(BrowserLaunchPreflightRefusedSlugSchema).optional()
})
export type CreateProvisioningDispatchResponse = z.infer<typeof CreateProvisioningDispatchResponseSchema>

// GET /v1/mobile-apps/provisioning-runs/:id/launch-plan
//
// The browser-step launch module's answer for every manifest step, computed without writing
// anything (SCH-2964 KTD7). The run console renders this instead of deriving runnability, deps or
// in-flight state itself, and its Run button sends a step's `dispatchStepIds`.

/** Why the launch module refuses a launch, or a step within one. */
export const BrowserLaunchRefusalReasonSchema = z.enum([
  'NO_RUNNER_TARGET',
  'NOT_AVAILABLE',
  'RUN_NOT_FOUND',
  'APP_NOT_FOUND',
  'NOT_RUNNABLE',
  'NO_READY_STEPS',
  'DISPATCH_IN_FLIGHT',
  'PREFLIGHT_REFUSED',
  'DATABASE_ERROR'
])
export type BrowserLaunchRefusalReason = z.infer<typeof BrowserLaunchRefusalReasonSchema>

/** What kind of input a requirement names: drives the icon and the wording, not the verdict. */
export const BrowserStepRequirementKindSchema = z.enum([
  'listing-assets',
  'listing-text',
  'operator-input',
  'vault-credential',
  'backend-mode'
])
export type BrowserStepRequirementKind = z.infer<typeof BrowserStepRequirementKindSchema>

/**
 * Something a step needs beyond its deps. `blocking: true` means the step cannot run from a
 * dispatch at all and is refused `NOT_RUNNABLE`; `false` is advisory context on a step that runs.
 * One `id` can touch several steps, which the graph draws as one shared requirement node.
 */
export const BrowserStepRequirementSchema = z.object({
  id: z.string(),
  kind: BrowserStepRequirementKindSchema,
  label: z.string(),
  detail: z.string(),
  blocking: z.boolean()
})
export type BrowserStepRequirement = z.infer<typeof BrowserStepRequirementSchema>

export const ProvisioningLaunchPlanStepSchema = z.object({
  stepId: z.string(),
  verdict: z.enum(['runnable', 'refused']),
  /** Set when `verdict` is `refused`. */
  reason: BrowserLaunchRefusalReasonSchema.optional(),
  /** The operator-facing explanation of `reason`. */
  detail: z.string().optional(),
  requirements: z.array(BrowserStepRequirementSchema),
  /**
   * What Run sends for this step: the step plus its unmet, fresh-aware deps, in manifest order.
   * Just the step when every dep has succeeded inside the staleness window.
   */
  dispatchStepIds: z.array(z.string())
})
export type ProvisioningLaunchPlanStep = z.infer<typeof ProvisioningLaunchPlanStepSchema>

export const ProvisioningLaunchPlanSchema = z.object({
  /**
   * A refusal that applies to the whole run (no runner target, not available on this host). Shown
   * once; the steps then carry no per-step reason for it.
   */
  runWide: z.object({ reason: BrowserLaunchRefusalReasonSchema, message: z.string() }).nullable(),
  /** Every manifest step, in manifest order. */
  steps: z.array(ProvisioningLaunchPlanStepSchema)
})
export type ProvisioningLaunchPlan = z.infer<typeof ProvisioningLaunchPlanSchema>

export const GetProvisioningLaunchPlanResponseSchema = z.object({
  message: z.string(),
  data: ProvisioningLaunchPlanSchema
})
export type GetProvisioningLaunchPlanResponse = z.infer<typeof GetProvisioningLaunchPlanResponseSchema>

// GET /v1/mobile-apps/provisioning-runs/:id/dispatches

export const ListProvisioningDispatchesResponseSchema = z.object({
  message: z.string(),
  data: z.array(ProvisioningDispatchResponseSchema)
})
export type ListProvisioningDispatchesResponse = z.infer<typeof ListProvisioningDispatchesResponseSchema>

// POST /v1/mobile-apps/provisioning-runs/test-login
//
// Dev-only ASC login smoke test: spawns `journey login-check --headful`
// detached on the backend host. Fire-and-forget — the 202 means the process
// was handed to the OS, not that the login succeeded.

export const TestProvisioningLoginResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    spawned: z.literal(true),
    /** Repo-relative path where the signed-in screenshot lands. */
    screenshotPath: z.string()
  })
})
export type TestProvisioningLoginResponse = z.infer<typeof TestProvisioningLoginResponseSchema>

// POST /internal/mobile-apps/provisioning-runs/dispatches/:dispatchId/report
//
// The runner's progress callback. `kind: 'started'` binds the GitHub run id on
// the first call; `kind: 'step'` records one step transition; `kind:
// 'finished'` rolls the dispatch up to a terminal status. Gate closure (retry /
// manual-complete against the orchestrator) stays the CLI's job — this endpoint
// only records.

export const ReportProvisioningDispatchRequestSchema = z.object({
  kind: z.enum(['started', 'step', 'finished']),
  stepId: z.string().optional(),
  stepStatus: BrowserStepRunStatusSchema.optional(),
  phase: z.string().optional(),
  message: z.string().optional(),
  output: z.unknown().optional(),
  /**
   * Pointers to the artifacts a faulted step left behind (U48). KEYS ONLY — the
   * bytes went straight to S3 through a presigned PUT, precisely so that a
   * customer's portal page never travels in this body.
   */
  evidence: StepEvidenceSchema.optional()
})
export type ReportProvisioningDispatchRequest = z.infer<typeof ReportProvisioningDispatchRequestSchema>

// POST /internal/mobile-apps/provisioning-runs/dispatches/:dispatchId/evidence-uploads
//
// Track I, U48. The runner cannot write to S3 and must not be given the ability:
// it declares no AWS SDK and its task role grants nothing. So it asks the backend
// for a short-lived presigned PUT scoped to one dispatch step, and the retention
// and access limits stay server-enforced.
//
// The SERVER decides the key and the content type; the runner supplies only
// which step and which of the two artifact kinds. A runner-chosen key would let
// a compromised task write anywhere in the prefix, and a runner-chosen content
// type would let it file a portal page as something a browser will execute.

export const CreateStepEvidenceUploadRequestSchema = z.object({
  stepId: z.string().min(1),
  kind: StepEvidenceKindSchema,
  /** Bytes about to be PUT — refused above the per-kind ceiling before any URL is minted. */
  contentLength: z.number().int().positive()
})
export type CreateStepEvidenceUploadRequest = z.infer<typeof CreateStepEvidenceUploadRequestSchema>

export const CreateStepEvidenceUploadResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    kind: StepEvidenceKindSchema,
    /** The key to report back on the step record once the PUT succeeds. */
    key: z.string(),
    url: z.string(),
    /** Must be sent verbatim as the PUT's Content-Type — it is part of the signature. */
    contentType: z.string(),
    expiresAt: z.string()
  })
})
export type CreateStepEvidenceUploadResponse = z.infer<typeof CreateStepEvidenceUploadResponseSchema>

// GET /v1/mobile-apps/provisioning-runs/dispatches/:dispatchId/steps/:stepId/evidence
//
// The read side, behind the same gate as the trace link: super-admin only,
// presigned, expiring. It hands back LINKS, never bytes, so the console renders
// an inspector entry rather than a bare URL.

export const StepEvidenceLinkSchema = z.object({
  kind: StepEvidenceKindSchema,
  key: z.string(),
  url: z.string(),
  expiresAt: z.string()
})
export type StepEvidenceLink = z.infer<typeof StepEvidenceLinkSchema>

export const GetStepEvidenceResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    stepId: z.string(),
    /** Null when the step recorded no evidence — a succeeded or blocked step never does. */
    capturedAt: z.string().nullable(),
    provenance: StepEvidenceSchema.shape.provenance.nullable(),
    /** Empty when nothing was captured; one entry per artifact that made it to S3. */
    links: z.array(StepEvidenceLinkSchema)
  })
})
export type GetStepEvidenceResponse = z.infer<typeof GetStepEvidenceResponseSchema>

// GET /internal/mobile-apps/provisioning-runs/recent?schoolId=…
//
// Whether a school had a provisioning run created in the last 24 hours, read from the ops store
// (SCH-2664). The `pnpm sj onesignal` check asks it to tell a white-label app that has not replicated
// yet from the shared-app default.

/** The window `hadRecentRun` covers. */
export const RECENT_PROVISIONING_RUN_WINDOW_HOURS = 24

export const RecentProvisioningRunQuerySchema = z.object({
  /** The school's PUBLIC id — the `schoolId` a run's inputs carry. */
  schoolId: z.string().min(1)
})
export type RecentProvisioningRunQuery = z.infer<typeof RecentProvisioningRunQuerySchema>

export const RecentProvisioningRunResponseSchema = z.object({
  schoolId: z.string(),
  hadRecentRun: z.boolean(),
  windowHours: z.number().int().positive()
})
export type RecentProvisioningRunResponse = z.infer<typeof RecentProvisioningRunResponseSchema>
